import React, { useState, useRef, useEffect } from "react";
import { Globe, Check, ChevronDown } from "lucide-react";
import { LANGUAGES, useI18n } from "@/i18n";

export default function LanguageSwitcher({ variant = "header" }) {
  const { lang, setLang } = useI18n();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const current = LANGUAGES.find((l) => l.code === lang) || LANGUAGES[0];

  if (variant === "mobile") {
    return (
      <div className="grid grid-cols-2 gap-2 py-2">
        {LANGUAGES.map((l) => {
          const active = l.code === lang;
          return (
            <button
              key={l.code}
              data-testid={`lang-mobile-${l.code}`}
              onClick={() => setLang(l.code)}
              className={`px-3 py-2 rounded-md text-sm border flex items-center justify-between transition ${
                active
                  ? "border-[#38D8FF] bg-[#38D8FF]/10 text-white"
                  : "border-white/15 text-white/75 hover:border-white/30"
              }`}
            >
              <span className="flex items-center gap-2">
                <span>{l.flag}</span>
                {l.short}
              </span>
              {active && <Check className="w-3.5 h-3.5 text-[#38D8FF]" />}
            </button>
          );
        })}
      </div>
    );
  }

  return (
    <div ref={ref} className="relative">
      <button
        data-testid="lang-switcher-trigger"
        onClick={() => setOpen(!open)}
        className="inline-flex items-center gap-2 px-3 py-2 rounded-md text-sm text-white/75 hover:text-white border border-white/10 hover:border-white/25 transition"
        aria-label="Change language"
      >
        <Globe className="w-3.5 h-3.5" />
        <span className="hidden md:inline nx-mono text-xs tracking-[0.18em]">
          {current.short}
        </span>
        <ChevronDown className={`w-3.5 h-3.5 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-2 w-48 z-50">
          <div className="nx-card rounded-xl p-1.5 shadow-2xl">
            {LANGUAGES.map((l) => {
              const active = l.code === lang;
              return (
                <button
                  key={l.code}
                  data-testid={`lang-option-${l.code}`}
                  onClick={() => {
                    setLang(l.code);
                    setOpen(false);
                  }}
                  className={`w-full flex items-center justify-between px-3 py-2 rounded-md text-sm transition ${
                    active ? "bg-white/5 text-white" : "text-white/80 hover:bg-white/5 hover:text-white"
                  }`}
                >
                  <span className="flex items-center gap-3">
                    <span className="text-base leading-none">{l.flag}</span>
                    <span>{l.label}</span>
                  </span>
                  {active && <Check className="w-4 h-4 text-[#38D8FF]" />}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
